import CommandInterceptor from 'diagram-js/lib/command/CommandInterceptor';
import EventBus from 'diagram-js/lib/core/EventBus';
import {PathwaysModeling} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling';
import {CommandType} from '@ispm/shared/bpmn-io/bpmn-js';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {State} from '@ispm-client/modules/bpmn-modeler/store/state';
import {select, Store} from '@ngrx/store';
import {selectNodeCreationInfoById} from '@ispm-client/modules/bpmn-modeler/store/selectors';
import {map, take} from 'rxjs/operators';
import {map as lodashMap, without} from 'lodash';
import {nodesDetailsExtensionType, taskType} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {Shape} from 'diagram-js/lib/model';
import {saveClear} from 'diagram-js/lib/util/Removal';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {BpmnExtensionHelper} from '@ispm/shared/bpmn-io/bpmn-extension-helper';
import {
  ifNodeOfTypesByNodeDetails,
  isConnection
} from '@ispm-client/modules/bpmn-modeler/bpmn/Interceptors/command-interceptor-utils';
import {is} from 'bpmn-js/lib/util/ModelUtil';

export class ResetParentDataBehavior extends CommandInterceptor {


  constructor(eventBus: EventBus, private pathwaysModeling: PathwaysModeling, private elementRegistry,
              private modeling, private store$: Store<State>, private bpmnReplace,
              private bpmnExtensionHelper: BpmnExtensionHelper) {
    super(eventBus);
    super.postExecuted([CommandType.NODE_UPDATE_DATA, CommandType.NODE_CREATE_DATA], ifNodeOfTypesByNodeDetails(this.reCreateChildrenData.bind(this), [NodeType.normal, NodeType.neighborhood_entry]));
    super.preExecuted([CommandType.REMOVE_NODE], ifNodeOfTypesByNodeDetails(this.cacheParentIds.bind(this), [NodeType.normal, NodeType.neighborhood_entry]));
    super.preExecuted([CommandType.REMOVE_CONNECTION], isConnection(this.cacheParentIds.bind(this)));
    super.postExecuted([CommandType.REMOVE_NODE], ifNodeOfTypesByNodeDetails(this.updateNodeChildrenData.bind(this), [NodeType.normal, NodeType.neighborhood_entry]));
    super.postExecuted([CommandType.REMOVE_CONNECTION], isConnection(this.updateNodeChildrenData.bind(this)));

  }

  private updateNodePosition(element: Shape, parent: Shape) {
    const nodeDetails = ModelingUtil.getExtension(element, nodesDetailsExtensionType);
    nodeDetails.nodePosition = {
      x: parent.x,
      y: parent.y
    };
    this.bpmnExtensionHelper.addOrReplaceExtension(element, nodeDetails);
  }

  updateNodeChildrenData(event) {
    const context = event.context;
    const element = context.element;
    const nodeType = ModelingUtil.getNodeType(element);
    if (context.nodeParentIds !== undefined && nodeType !== NodeType.treatment) {
      context.nodeParentIds.map(parentId => this.elementRegistry.get(parentId)).forEach(parentElement => {
        if (parentElement) {
          if (!ModelingUtil.hasDeletionTrackingChange(parentElement) && this.needToConvertFromSubProcessToTask(parentElement)) {
            this.removeChildren(parentElement);
            this.removeQuestions(parentElement);
            const target = {type: taskType};
            const newElement = this.bpmnReplace.replaceElement(parentElement, target);
            this.updateNodePosition(newElement, parentElement);
          } else {
            this.resetParentChildrenData(parentElement);
          }
        }
      });
    }
  }

  private needToConvertFromSubProcessToTask(node): boolean {
    //Bpmn node is task if it has no children, or its connected to TP node - node without condition.
    const directChildren = ModelingUtil.getDirectTreeChildrenNodes(node);
    return !is(node, taskType) && ((directChildren.length === 0) || (directChildren.length === 1 && ModelingUtil.getNodeType(directChildren[0]) === NodeType.treatment));
  }

  public removeChildren(parentElement: Shape) {
    // remove child shapes and connections
    saveClear(parentElement.children, (child) => {
      if (ModelingUtil.isConnection(child)) {
        this.pathwaysModeling.removeConnection(child, {nested: true});
      } else {
        this.modeling.removeShape(child, {nested: true});
      }
    });

  }

  cacheParentIds(event) {
    const context = event.context;
    const element = context.element;
    if (element) {
      context.nodeParentIds = ModelingUtil.getNodeParentsIds(element);

    }
  }

  private reCreateChildrenData(event) {
    const context = event.context;
    const element = context.element;
    const parents = ModelingUtil.getNodeParents(element);
    parents.forEach(parentElement => {
      this.resetParentChildrenData(parentElement);
    });

  }

  private resetParentChildrenData(parentElement) {
    const childrenNodesIds = ModelingUtil.getDirectTreeChildrenNodesIds(parentElement);
    this.getChildrenData$(childrenNodesIds)
      .subscribe(childrenDataArray => {
        this.pathwaysModeling.resetNodeChildrenData(parentElement, childrenDataArray);
      });
  }

  private getChildrenData$(childrenNodesIds: Array<string>) {
    return this.store$.pipe(select(selectNodeCreationInfoById(childrenNodesIds)),
      map(nodeCreationInfoArray => without(lodashMap(nodeCreationInfoArray, 'data'), null, undefined)),
      take(1));
  }

  private removeQuestions(parentElement) {
    const nodeDetails = ModelingUtil.getNodeDetails(parentElement);
    delete nodeDetails.questions;
    this.bpmnExtensionHelper.replaceExtension(parentElement, nodeDetails);
  }


}

ResetParentDataBehavior.$inject = [...CommandInterceptor.$inject, 'pathwaysModeling', 'elementRegistry', 'modeling',
  'store$', 'bpmnReplace', 'bpmnExtensionHelper'];
